/**
 * useAutosave — fires `save(value)` a debounced interval after `value`
 * changes, skipping the initial mount and any change that matches the last
 * successfully-saved snapshot.
 *
 * Returns { status: "idle" | "saving" | "saved" | "error", savedAt, saveNow }
 * so callers can render a subtle status pill alongside the manual Save
 * button. The optimistic UI is up to the caller — this hook only manages
 * the debounce, dedupe, and status.
 */
import { useEffect, useRef, useState, useCallback } from "react";

export function useAutosave(value, save, { delay = 900, enabled = true, isEqual } = {}) {
  const [status, setStatus] = useState("idle");
  const [savedAt, setSavedAt] = useState(null);
  const savedSnapshotRef = useRef(null);
  const initializedRef = useRef(false);
  const timerRef = useRef(null);
  const inflightRef = useRef(false);
  const saveRef = useRef(save);
  saveRef.current = save;

  const equalFn = isEqual || ((a, b) => JSON.stringify(a) === JSON.stringify(b));

  const runSave = useCallback(async (payload) => {
    if (inflightRef.current) return;
    inflightRef.current = true;
    setStatus("saving");
    try {
      await saveRef.current(payload);
      savedSnapshotRef.current = payload;
      setSavedAt(new Date());
      setStatus("saved");
    } catch {
      setStatus("error");
    } finally {
      inflightRef.current = false;
    }
  }, []);

  // Seed the "last saved" snapshot from the first non-empty value we see so
  // hydration doesn't trigger a spurious save.
  useEffect(() => {
    if (initializedRef.current) return;
    if (value && Object.keys(value).length > 0) {
      savedSnapshotRef.current = value;
      initializedRef.current = true;
    }
  }, [value]);

  useEffect(() => {
    if (!enabled) return;
    if (!initializedRef.current) return;
    if (equalFn(value, savedSnapshotRef.current)) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => runSave(value), delay);
    return () => timerRef.current && clearTimeout(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, delay, enabled]);

  const saveNow = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (!initializedRef.current) return;
    if (equalFn(value, savedSnapshotRef.current)) return;
    return runSave(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, runSave]);

  return { status, savedAt, saveNow };
}
